import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { fmtNumber, isAtLeastAdmin } from '@/lib/utils';
import { Loader2, Save, Building2, Users, Plus, Shield, Trash2, UserPlus, Crown, Eye, Wrench, Bell, Mail, CheckCircle2, Upload, Image, RotateCw, Clock, BrainCircuit, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USAGE_RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

function usageFromISO(days) {
  if (days === 'all') return null;
  return new Date(Date.now() - Number(days) * 86400000).toISOString();
}

function PendingInvitations() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchInvites = async () => {
    try { const res = await fetch(`${API}/team/invitations`, { credentials: 'include' }); if (res.ok) setInvites(await res.json()); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { fetchInvites(); }, []);
  const resend = async (id) => {
    try { await fetch(`${API}/team/invitations/${id}/resend`, { method: 'POST', credentials: 'include' }); alert('Invitation resent!'); }
    catch (e) {}
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this invitation?')) return;
    try { await fetch(`${API}/team/invitations/${id}`, { method: 'DELETE', credentials: 'include' }); setInvites(p => p.filter(i => i.invite_id !== id)); }
    catch (e) {}
  };
  if (loading) return null;
  if (invites.length === 0) return null;
  return (
    <Card className="border-[#E2E8ED] shadow-none mt-4">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-[#0D1830]" />Pending Invitations ({invites.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {invites.map(inv => (
            <div key={inv.invite_id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8ED] bg-[#F7FAFB]" data-testid={`invite-${inv.invite_id}`}>
              <div>
                <span className="text-xs font-medium">{inv.email}</span>
                <span className="text-[10px] text-[#72A4BF] ml-2">as {inv.role}</span>
                <p className="text-[10px] text-[#72A4BF]/60">Sent {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => resend(inv.invite_id)} className="p-1.5 rounded-lg hover:bg-[#72A4BF]/10" title="Resend" data-testid={`resend-${inv.invite_id}`}><RotateCw className="w-3 h-3 text-[#0D1830]" /></button>
                <button onClick={() => revoke(inv.invite_id)} className="p-1.5 rounded-lg hover:bg-red-50" title="Revoke" data-testid={`revoke-${inv.invite_id}`}><Trash2 className="w-3 h-3 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700', icon: Crown, desc: 'Full access to everything' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-700', icon: Shield, desc: 'Manage projects, quotes, team' },
  estimator: { label: 'Estimator', color: 'bg-[#72A4BF]/10 text-[#0D1830]', icon: Wrench, desc: 'Create projects, run analysis, quotes' },
  viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-600', icon: Eye, desc: 'Read-only access' },
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [form, setForm] = useState({ company_name: '', address: '', city: '', province: '', postal_code: '', phone: '', email: '', website: '', industry: 'Construction', logo_url: '', default_tax_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);
  // Team
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('estimator');
  const [inviting, setInviting] = useState(false);
  // Notifications
  const [notifPrefs, setNotifPrefs] = useState({
    email_per_project: true,
    daily_digest: false,
    in_app_notifications: true,
    email_provider: 'resend'
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaved, setNotifSaved] = useState(false);
  // Usage (Nivela Tokens — tenant-scoped, tokens only: no USD/CAD/rates)
  const [usageData, setUsageData] = useState({ runs: [], total: 0, summary: { run_count: 0, nivela_tokens_total: 0 } });
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageDays, setUsageDays] = useState('30');
  const [usageOffset, setUsageOffset] = useState(0);
  const USAGE_LIMIT = 50;
  // Billing (Nivela wallet — tenant-scoped, no USD)
  const [billingInfo, setBillingInfo] = useState(null);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/company`, { credentials: 'include' });
        if (res.ok) { const data = await res.json(); setForm(prev => ({ ...prev, ...data })); }
      } catch (e) {}
      finally { setLoading(false); }
    };
    fetch_();
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/company/logo`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, logo_url: data.logo_url }));
      }
    } catch (e) {}
    finally { setUploadingLogo(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/company`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (e) {}
    finally { setSaving(false); }
  };

  const fetchTeam = async () => {
    setTeamLoading(true);
    try {
      const res = await fetch(`${API}/team`, { credentials: 'include' });
      if (res.ok) setTeam(await res.json());
    } catch (e) {}
    finally { setTeamLoading(false); }
  };

  useEffect(() => { if (activeTab === 'team') fetchTeam(); }, [activeTab]);

  const fetchNotifPrefs = async () => {
    try {
      const res = await fetch(`${API}/notification-prefs`, { credentials: 'include' });
      if (res.ok) setNotifPrefs(await res.json());
    } catch (e) {}
  };

  useEffect(() => { if (activeTab === 'notifications') fetchNotifPrefs(); }, [activeTab]);

  const fetchUsage = async (reset = true) => {
    setUsageLoading(true);
    const offset = reset ? 0 : usageOffset + USAGE_LIMIT;
    try {
      const params = new URLSearchParams({ limit: String(USAGE_LIMIT), offset: String(offset) });
      const from = usageFromISO(usageDays);
      if (from) params.set('from', from);
      const res = await fetch(`${API}/dashboard/usage?${params.toString()}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsageData(prev => ({
          ...data,
          runs: reset ? (data.runs || []) : [...(prev.runs || []), ...(data.runs || [])],
        }));
        setUsageOffset(offset);
      }
    } catch (e) {}
    finally { setUsageLoading(false); }
  };

  const fetchBilling = async () => {
    try {
      const res = await fetch(`${API}/dashboard/billing`, { credentials: 'include' });
      if (res.ok) setBillingInfo(await res.json());
    } catch (e) {}
  };

  const [portalLoading, setPortalLoading] = useState(false);
  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${API}/billing/portal`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) { window.location.href = data.url; return; }
    } catch (e) {}
    setPortalLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'usage') { fetchUsage(true); fetchBilling(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usageDays]);

  // Surface the Stripe Checkout return state once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const b = params.get('billing');
    if (b === 'success') {
      setActiveTab('usage');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (b === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const saveNotifPrefs = async () => {
    setNotifLoading(true);
    try {
      const res = await fetch(`${API}/notification-prefs`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(notifPrefs)
      });
      if (res.ok) { setNotifSaved(true); setTimeout(() => setNotifSaved(false), 2000); }
    } catch (e) {}
    finally { setNotifLoading(false); }
  };

  const inviteMember = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await fetch(`${API}/team/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email: inviteEmail, name: inviteName, role: inviteRole })
      });
      if (res.ok) { setShowInvite(false); setInviteEmail(''); setInviteName(''); fetchTeam(); }
      else { const err = await res.json(); alert(err.detail || 'Error'); }
    } catch (e) {}
    finally { setInviting(false); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await fetch(`${API}/team/${userId}/role`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      fetchTeam();
    } catch (e) {}
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this team member?')) return;
    await fetch(`${API}/team/${userId}`, { method: 'DELETE', credentials: 'include' });
    fetchTeam();
  };

  // Tenant-level admin gating: tenant admin OR a superadmin logged into their own tenant.
  const isAdmin = isAtLeastAdmin(user);

  return (
    <div className="min-h-full bg-[#F7FAFB]" data-testid="settings-page">
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-8">
        <h1 className="text-2xl font-bold text-[#0D1830] mb-2">Settings</h1>
        <p className="text-sm text-[#72A4BF] mb-6">Company profile and team management</p>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E2E8ED] rounded-xl p-1 mb-6">
            <TabsTrigger value="company" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white" data-testid="tab-company">
              <Building2 className="w-3.5 h-3.5 mr-1.5" />Company
            </TabsTrigger>
            <TabsTrigger value="team" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white" data-testid="tab-team">
              <Users className="w-3.5 h-3.5 mr-1.5" />Team & Roles
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="usage" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white" data-testid="tab-usage">
                <BrainCircuit className="w-3.5 h-3.5 mr-1.5" />Usage
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white" data-testid="tab-notifications">
              <Bell className="w-3.5 h-3.5 mr-1.5" />Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="company">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>
            ) : (
              <Card className="border-[#E2E8ED] shadow-none">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-[#0D1830]" />Company Profile</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {/* Logo upload */}
                  <div>
                    <label className="text-xs font-medium text-[#446E87] mb-2 block">Company Logo</label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-[#E2E8ED] flex items-center justify-center overflow-hidden bg-[#F7FAFB]" data-testid="logo-preview">
                        {form.logo_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                          <Image className="w-8 h-8 text-[#DDD]" />
                        )}
                      </div>
                      <div>
                        <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                        <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} className="rounded-full text-xs" data-testid="upload-logo-btn">
                          {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                          {form.logo_url ? 'Change Logo' : 'Upload Logo'}
                        </Button>
                        <p className="text-[10px] text-[#72A4BF] mt-1">PNG, JPG, SVG. Max 2MB. Used in exports.</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2"><label className="text-xs font-medium text-[#446E87] mb-1 block">Company Name</label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Your Company Name" data-testid="company-name-input" /></div>
                    <div className="col-span-2"><label className="text-xs font-medium text-[#446E87] mb-1 block">Address</label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                    <div><label className="text-xs font-medium text-[#446E87] mb-1 block">City</label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                    <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Province/State</label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
                    <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Postal Code</label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
                    <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Phone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Email</label><Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="company@example.com" data-testid="company-email-input" /></div>
                    <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Website</label><Input value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.example.com" data-testid="company-website-input" /></div>
                  </div>

                  {/* Default Tax Rate */}
                  <div className="border border-[#E2E8ED] rounded-xl p-4 bg-[#F7FAFB]/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-xs font-semibold text-[#0D1830] block">Default Tax Rate (%)</label>
                        <p className="text-[10px] text-[#72A4BF] mt-0.5">Auto-applied to every new quote. Can be overridden per quote.</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number" step="0.01" min="0" max="100"
                          value={form.default_tax_rate || ''}
                          onChange={(e) => setForm({ ...form, default_tax_rate: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                          className="w-24 h-9 text-sm text-right"
                          data-testid="default-tax-rate-input"
                        />
                        <span className="text-xs text-[#72A4BF]">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4">
                    {saved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
                    <Button onClick={save} disabled={saving} className="bg-[#0D1830] text-white rounded-full px-6 hover:opacity-90" data-testid="save-settings-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Save
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="team" data-testid="team-tab">
            {/* Role legend */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {Object.entries(ROLE_CONFIG).map(([role, cfg]) => (
                <Card key={role} className="border-[#E2E8ED] shadow-none">
                  <CardContent className="p-3 flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${cfg.color} flex items-center justify-center`}>
                      <cfg.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#0D1830]">{cfg.label}</p>
                      <p className="text-[10px] text-[#72A4BF]">{cfg.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-[#E2E8ED] shadow-none">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#0D1830]" />Team Members
                  </CardTitle>
                  {isAdmin && (
                    <Button size="sm" onClick={() => setShowInvite(true)} className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" data-testid="invite-btn">
                      <UserPlus className="w-3.5 h-3.5 mr-1" />Invite
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {teamLoading ? (
                  <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-[#1D4053] animate-spin" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E2E8ED]">
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                        <TableHead className="text-xs">Role</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        {isAdmin && <TableHead className="text-xs w-24">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {team.map((member) => {
                        const rc = ROLE_CONFIG[member.role] || ROLE_CONFIG.viewer;
                        const isMe = member.user_id === user?.user_id;
                        return (
                          <TableRow key={member.user_id} className="border-[#F0F4F7]">
                            <TableCell className="text-xs font-medium">
                              <div className="flex items-center gap-2">
                                {member.picture ? <img src={member.picture} alt="" className="w-6 h-6 rounded-full" /> : (
                                  <div className="w-6 h-6 rounded-full bg-[#1D4053]/20 flex items-center justify-center text-[10px] font-bold text-[#0D1830]">
                                    {(member.name || '?')[0].toUpperCase()}
                                  </div>
                                )}
                                {member.name} {isMe && <span className="text-[10px] text-[#72A4BF]/60">(you)</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-[#72A4BF]">{member.email}</TableCell>
                            <TableCell>
                              {isAdmin && !isMe ? (
                                <select value={member.role || 'viewer'} onChange={(e) => changeRole(member.user_id, e.target.value)}
                                  className="text-xs rounded-full border border-[#E2E8ED] px-2 py-0.5 bg-white" data-testid={`role-select-${member.user_id}`}>
                                  <option value="admin">Admin</option>
                                  <option value="manager">Manager</option>
                                  <option value="estimator">Estimator</option>
                                  <option value="viewer">Viewer</option>
                                </select>
                              ) : (
                                <Badge className={`${rc.color} border-0 text-xs rounded-full px-2 py-0.5`}>{rc.label}</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={`border-0 text-xs rounded-full px-2 py-0.5 ${member.status === 'invited' ? 'bg-[#72A4BF]/10 text-[#0D1830]' : 'bg-[#E8F5E9] text-[#2E7D32]'}`}>
                                {member.status === 'invited' ? 'Invited' : 'Active'}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                {!isMe && (
                                  <button onClick={() => removeMember(member.user_id)} className="p-1 rounded hover:bg-red-50" data-testid={`remove-member-${member.user_id}`}>
                                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                  </button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Pending Invitations */}
            {isAdmin && (
              <PendingInvitations />
            )}
          </TabsContent>

          {/* Usage Tab — Nivela Tokens balance + per-run consumption (no USD) */}
          {isAdmin && (
            <TabsContent value="usage" data-testid="usage-tab">
              <div className="space-y-4">
                {/* Subscription status + actions */}
                {billingInfo && (() => {
                  const sub = billingInfo.subscription_status;
                  const subscribed = sub === 'active';
                  const needsAttention = sub === 'past_due' || sub === 'unpaid';
                  return (
                    <Card className="border-[#E2E8ED] shadow-none">
                      <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Subscription</p>
                          <p className="text-sm font-semibold text-[#0D1830] mt-0.5">
                            {subscribed ? (billingInfo.plan_name || 'Active plan')
                              : needsAttention ? 'Payment needs attention'
                              : 'No active subscription'}
                            <span className={`ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                              subscribed ? 'bg-emerald-100 text-emerald-700'
                              : needsAttention ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-600'
                            }`}>{sub || 'none'}</span>
                          </p>
                          {needsAttention && (
                            <p className="text-[10px] text-amber-700 mt-1">Your last payment failed — update your card to keep running analyses.</p>
                          )}
                        </div>
                        {subscribed || needsAttention ? (
                          <Button size="sm" onClick={openBillingPortal} disabled={portalLoading}
                            className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" data-testid="manage-billing-btn">
                            {portalLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Manage on Stripe
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => { window.location.href = '/billing/plans'; }}
                            className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" data-testid="choose-plan-btn">
                            Choose a plan
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Out-of-tokens banner */}
                {billingInfo && Number(billingInfo.balance) <= 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700" data-testid="out-of-tokens-banner">
                    <span className="font-semibold">You're out of Nivela Tokens.</span> Contact your Nivela administrator to recharge before running more analyses.
                  </div>
                )}

                {/* Wallet balance */}
                {billingInfo && (
                  <Card className="border-[#E2E8ED] shadow-none">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Available</p>
                          <p className="text-3xl font-black text-[#0D1830] mt-1 leading-none">
                            {fmtNumber(billingInfo.balance || 0)}
                            <span className="text-xs font-medium text-[#72A4BF] ml-2">Nivela Tokens</span>
                          </p>
                          {billingInfo.plan_name && (
                            <p className="text-[10px] text-[#72A4BF] mt-2">Plan: <span className="text-[#0D1830] font-medium">{billingInfo.plan_name}</span></p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-[#72A4BF]">This cycle (base)</p>
                          <p className="text-sm font-semibold text-[#0D1830]">
                            {fmtNumber(billingInfo.base_remaining || 0)} <span className="text-[10px] text-[#72A4BF]">of {fmtNumber(billingInfo.base_allowance || 0)}</span>
                          </p>
                          <p className="text-[10px] text-[#72A4BF] mt-2">Top-up balance</p>
                          <p className="text-sm font-semibold text-[#0D1830]">{fmtNumber(billingInfo.topup_balance || 0)}</p>
                        </div>
                      </div>
                      {(billingInfo.cycle_start || billingInfo.cycle_end) && (
                        <p className="text-[10px] text-[#72A4BF]/80 mt-3 border-t border-[#F0F4F7] pt-2">
                          Cycle: {billingInfo.cycle_start?.slice(0,10) || '—'} → {billingInfo.cycle_end?.slice(0,10) || '—'}.
                          Base tokens reset at cycle end; <span className="font-semibold">top-up tokens carry forward indefinitely</span>.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Billing details + last invoice — synced back from Stripe so
                    the user sees the details they entered on Checkout and the
                    status of their most recent invoice. Everything mutable
                    (card, address, cancellation, invoice history) is on the
                    Stripe Customer Portal — the "Manage on Stripe" button. */}
                {billingInfo && (billingInfo.billing_name || billingInfo.billing_address || billingInfo.last_invoice) && (
                  <Card className="border-[#E2E8ED] shadow-none" data-testid="billing-details-card">
                    <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Billed to</p>
                        <p className="text-xs font-semibold text-[#0D1830] mt-0.5">
                          {billingInfo.billing_name || billingInfo.billing_email || '—'}
                        </p>
                        {billingInfo.billing_email && billingInfo.billing_name && (
                          <p className="text-[10px] text-[#72A4BF]">{billingInfo.billing_email}</p>
                        )}
                        {billingInfo.billing_address && (
                          <p className="text-[10px] text-[#72A4BF] mt-1 leading-snug">
                            {[
                              billingInfo.billing_address.line1,
                              billingInfo.billing_address.line2,
                              [billingInfo.billing_address.city, billingInfo.billing_address.state, billingInfo.billing_address.postal_code].filter(Boolean).join(' '),
                              billingInfo.billing_address.country,
                            ].filter(Boolean).map((line, i) => <span key={i} className="block">{line}</span>)}
                          </p>
                        )}
                        <p className="text-[10px] text-[#72A4BF]/70 mt-2">Edit on Stripe → "Manage on Stripe".</p>
                      </div>
                      {billingInfo.last_invoice && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Last invoice</p>
                          <p className="text-xs font-semibold text-[#0D1830] mt-0.5">
                            {fmtNumber((billingInfo.last_invoice.amount_paid_cents || 0) / 100)}{' '}
                            <span className="text-[10px] text-[#72A4BF] uppercase">{billingInfo.last_invoice.currency || 'cad'}</span>
                            <span className={`ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                              billingInfo.last_invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700'
                              : billingInfo.last_invoice.status === 'open' ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-600'
                            }`}>{billingInfo.last_invoice.status || '—'}</span>
                          </p>
                          {billingInfo.last_invoice.paid_at && (
                            <p className="text-[10px] text-[#72A4BF] mt-1">
                              Paid {new Date(billingInfo.last_invoice.paid_at).toLocaleDateString()}
                            </p>
                          )}
                          {billingInfo.last_invoice.hosted_invoice_url && (
                            <a href={billingInfo.last_invoice.hosted_invoice_url}
                              target="_blank" rel="noopener noreferrer"
                              className="text-[10px] text-[#1D4053] underline mt-2 inline-block"
                              data-testid="view-invoice-link">
                              View invoice on Stripe →
                            </a>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-xs text-[#446E87] bg-[#72A4BF]/10 rounded-xl px-3 py-2">
                    <BrainCircuit className="w-4 h-4 text-[#1D4053]" />
                    <span>Showing your analysis runs in Nivela Tokens</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={usageDays} onChange={(e) => setUsageDays(e.target.value)}
                      className="text-xs rounded-full border border-[#E2E8ED] px-3 py-1.5 bg-white" data-testid="usage-range-select">
                      {USAGE_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => fetchUsage(true)} className="h-8 text-[11px] px-2 gap-1">
                      <RefreshCw className={`w-3 h-3 ${usageLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Card className="border-[#E2E8ED] shadow-none">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center"><BrainCircuit className="w-5 h-5 text-[#1D4053]" /></div>
                      <div><p className="text-xl font-bold text-[#0D1830]">{fmtNumber(usageData.summary?.nivela_tokens_total || 0)}</p><p className="text-xs text-[#72A4BF]">Nivela Tokens consumed</p></div>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E2E8ED] shadow-none">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#F0F4F7] flex items-center justify-center"><RefreshCw className="w-5 h-5 text-[#446E87]" /></div>
                      <div><p className="text-xl font-bold text-[#0D1830]">{fmtNumber(usageData.summary?.run_count || 0)}</p><p className="text-xs text-[#72A4BF]">Analysis runs</p></div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                        <TableHead className="text-xs">Project</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs text-right w-16">Run #</TableHead>
                        <TableHead className="text-xs text-right">Nivela Tokens</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(usageData.runs || []).map(r => (
                        <TableRow key={r.run_id} className="border-[#F0F4F7]" data-testid={`usage-run-${r.run_id}`}>
                          <TableCell className="text-xs text-[#0D1830] max-w-[260px] truncate">{r.project_name || r.project_id}</TableCell>
                          <TableCell className="text-xs text-[#72A4BF] whitespace-nowrap">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</TableCell>
                          <TableCell className="text-xs text-right">{r.run_number ?? '—'}</TableCell>
                          <TableCell className="text-xs text-right font-medium">{fmtNumber(r.nivela_tokens || 0)}</TableCell>
                        </TableRow>
                      ))}
                      {(usageData.runs || []).length === 0 && (
                        <TableRow><TableCell colSpan={4} className="py-10 text-center text-xs text-[#72A4BF]">
                          {usageLoading ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#1D4053]" /> : 'No analysis runs in this period'}
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>

                {(usageData.runs || []).length < (usageData.total || 0) && (
                  <div className="flex justify-center">
                    <Button variant="outline" size="sm" onClick={() => fetchUsage(false)} disabled={usageLoading} className="h-8 text-[11px] px-3">
                      {usageLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Load more ({(usageData.runs || []).length} / {usageData.total})
                    </Button>
                  </div>
                )}

                <p className="text-[10px] text-[#72A4BF]/70">Nivela Tokens are consumed by AI analysis runs (including re-runs). The conversion rate is set by your Nivela administrator.</p>
              </div>
            </TabsContent>
          )}

          {/* Notifications Tab */}
          <TabsContent value="notifications" data-testid="notifications-tab">
            <Card className="border-[#E2E8ED] shadow-none">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="w-4 h-4 text-[#0D1830]" />Notification Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Email Notifications Section */}
                <div>
                  <h3 className="text-sm font-semibold text-[#0D1830] mb-4 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#446E87]" />Email Notifications
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl border border-[#E2E8ED] bg-[#F7FAFB]">
                      <div>
                        <p className="text-sm font-medium text-[#0D1830]">Per-Project Notifications</p>
                        <p className="text-xs text-[#72A4BF]">Receive an email when each project analysis completes</p>
                      </div>
                      <Switch 
                        checked={notifPrefs.email_per_project} 
                        onCheckedChange={(v) => setNotifPrefs(p => ({...p, email_per_project: v}))}
                        data-testid="toggle-email-per-project"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-xl border border-[#E2E8ED] bg-[#F7FAFB]">
                      <div>
                        <p className="text-sm font-medium text-[#0D1830]">Daily Digest</p>
                        <p className="text-xs text-[#72A4BF]">Receive a summary of all completed analyses at end of day</p>
                      </div>
                      <Switch 
                        checked={notifPrefs.daily_digest} 
                        onCheckedChange={(v) => setNotifPrefs(p => ({...p, daily_digest: v}))}
                        data-testid="toggle-daily-digest"
                      />
                    </div>
                  </div>
                </div>

                {/* In-App Notifications Section */}
                <div>
                  <h3 className="text-sm font-semibold text-[#0D1830] mb-4 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-[#446E87]" />In-App Notifications
                  </h3>
                  <div className="flex items-center justify-between p-4 rounded-xl border border-[#E2E8ED] bg-[#F7FAFB]">
                    <div>
                      <p className="text-sm font-medium text-[#0D1830]">Enable In-App Notifications</p>
                      <p className="text-xs text-[#72A4BF]">Show notifications in the app when analyses complete</p>
                    </div>
                    <Switch 
                      checked={notifPrefs.in_app_notifications} 
                      onCheckedChange={(v) => setNotifPrefs(p => ({...p, in_app_notifications: v}))}
                      data-testid="toggle-in-app"
                    />
                  </div>
                </div>

                {/* Email Provider Section */}
                <div>
                  <h3 className="text-sm font-semibold text-[#0D1830] mb-4">Email Service Provider</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setNotifPrefs(p => ({...p, email_provider: 'resend'}))}
                      className={`p-4 rounded-xl border text-left transition-all ${notifPrefs.email_provider === 'resend' ? 'border-[#1D4053] bg-[#72A4BF]/10' : 'border-[#E2E8ED] hover:border-[#E2E8ED]'}`}
                      data-testid="provider-resend"
                    >
                      <p className="text-sm font-semibold text-[#0D1830]">Resend</p>
                      <p className="text-xs text-[#72A4BF] mt-1">Modern email API, recommended for most users</p>
                    </button>
                    <button
                      onClick={() => setNotifPrefs(p => ({...p, email_provider: 'sendgrid'}))}
                      className={`p-4 rounded-xl border text-left transition-all ${notifPrefs.email_provider === 'sendgrid' ? 'border-[#1D4053] bg-[#72A4BF]/10' : 'border-[#E2E8ED] hover:border-[#E2E8ED]'}`}
                      data-testid="provider-sendgrid"
                    >
                      <p className="text-sm font-semibold text-[#0D1830]">SendGrid</p>
                      <p className="text-xs text-[#72A4BF] mt-1">Enterprise-grade, high-volume delivery</p>
                    </button>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E2E8ED]">
                  {notifSaved && (
                    <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />Saved!
                    </span>
                  )}
                  <Button onClick={saveNotifPrefs} disabled={notifLoading} className="bg-[#0D1830] text-white rounded-full px-6 hover:opacity-90" data-testid="save-notif-btn">
                    {notifLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Save Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Add a new member to your organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Email</label>
              <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="team@company.com" data-testid="invite-email-input" /></div>
            <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Name</label>
              <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Full name" data-testid="invite-name-input" /></div>
            <div><label className="text-xs font-medium text-[#446E87] mb-1 block">Role</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(ROLE_CONFIG).map(([role, cfg]) => (
                  <button key={role} onClick={() => setInviteRole(role)}
                    className={`p-2.5 rounded-xl border text-left transition-all ${inviteRole === role ? 'border-[#1D4053] bg-[#72A4BF]/10' : 'border-[#E2E8ED] hover:border-[#E2E8ED]'}`}
                    data-testid={`role-option-${role}`}>
                    <div className="flex items-center gap-2">
                      <cfg.icon className={`w-3.5 h-3.5 ${inviteRole === role ? 'text-[#0D1830]' : 'text-[#72A4BF]'}`} />
                      <span className="text-xs font-medium text-[#0D1830]">{cfg.label}</span>
                    </div>
                    <p className="text-[10px] text-[#72A4BF] mt-0.5 ml-5.5">{cfg.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)} className="rounded-full">Cancel</Button>
            <Button onClick={inviteMember} disabled={!inviteEmail || inviting} className="bg-[#0D1830] text-white rounded-full hover:opacity-90" data-testid="send-invite-btn">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UserPlus className="w-4 h-4 mr-1.5" />}Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
